import { useEffect, useRef, useState } from 'react'

import * as Styled from './styled'

import { t } from 'i18next'
import { shareLink } from 'utils/share'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { Box, TextField, Typography, useTheme } from '@mui/material'
import { MiscText } from 'components/login/styled'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'

export interface InviteLinkBadgeProps {
  link: string
}

export default function InviteLinkBadge(props: InviteLinkBadgeProps) {
  const theme = useTheme()

  if (!props.link) {
    return <></>
  }

  return (
    <Styled.InviteLinkBadge padding={3} gap={2}>
      <PopupSectionRow
        title={
          <Typography
            fontFamily={'NotoSans'}
            fontSize={'18px'}
            fontWeight={500}
          >
            {t('inviteOthersToJoin')}
          </Typography>
        }
        value=""
      />
      <MiscText
        mt={-2}
        fontSize={'14px'}
        fontWeight={'400 !important'}
        color={theme.palette.text.secondary}
      >
        {t('anyoneWithThisLink')}
      </MiscText>
      <InviteLinkRow width="90% !important" link={props.link} />
    </Styled.InviteLinkBadge>
  )
}

interface InviteLinkRowProps {
  link: string
  text?: string
  textSize?: string
  allowScroll?: boolean
  width?: string
  height?: string
}

export function InviteLinkRow(props: InviteLinkRowProps) {
  const theme = useTheme()
  const [linkCopyTitle, setLinkCopyTitle] = useState<string>(
    `${t('copyToClipboard')}`
  )

  const [showCopyDialog, setShowCopyDialog] = useState(false)

  async function handleCopyAction() {
    const textToCopy = props.text ?? props.link

    try {
      // Try to use the modern clipboard API
      await navigator.clipboard.writeText(textToCopy)

      // Copy succeeded - update UI
      setLinkCopyTitle(`${t('copied')}`)

      // Try to share AFTER copy succeeds (this is optional and should not block)
      try {
        await shareLink(textToCopy)
      } catch (shareError) {
        // Silently fail - share is a nice-to-have feature
        console.debug('Share not available:', shareError)
      }
    } catch (e) {
      // If clipboard API fails, show the manual copy dialog
      console.debug('Clipboard API failed, showing manual copy dialog:', e)
      setShowCopyDialog(true)
    }
  }

  return (
    <Styled.InviteLinkContainer
      height={props.height ?? '30px'}
      width={props.width}
      container
      dir="ltr"
    >
      <Styled.LinkContent
        id="invite-link"
        style={{
          fontSize: props.textSize ? props.textSize : '14px',
          textAlign: 'start'
        }}
        {...props}
      >
        {props.text ?? props.link}
      </Styled.LinkContent>
      <Styled.CopyLinkButton
        sx={{ backgroundColor: theme.palette.primary.main }}
        onClick={handleCopyAction}
      >
        {linkCopyTitle}
      </Styled.CopyLinkButton>
      <ConfirmationDialog
        title={t('copyEmbedCode')}
        content={<CopyDialogContent textToCopy={props.text ?? props.link} />}
        open={showCopyDialog}
        handleClose={() => setShowCopyDialog(false)}
        noButtons
        showCloseIcon
      />
    </Styled.InviteLinkContainer>
  )
}

interface CopyDialogContentProps {
  textToCopy: string
}

function CopyDialogContent(props: CopyDialogContentProps) {
  const textFieldRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Auto-select the text when the dialog opens
    if (textFieldRef.current) {
      textFieldRef.current.select()
      textFieldRef.current.focus()
    }
  }, [])

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <Typography width={'100%'} textAlign={'center'}>
        {t('codeAlreadySelected')}
      </Typography>
      <TextField
        inputRef={textFieldRef}
        value={props.textToCopy}
        fullWidth
        multiline
        InputProps={{
          readOnly: true
        }}
        onFocus={(e) => e.target.select()}
      />
    </Box>
  )
}
